import * as React from 'react';
import { orgToday } from '@/lib/today';
import { formatDuration, formatPeriod } from '@cfl/shared';

import { Alert, AlertDescription, Button, Skeleton } from '@/components/cargofl-ui';
import { useApprovalBoard, type BoardCell } from '@/lib/approvals-api';

/**
 * Who is behind, week by week.
 *
 * The approvals queue can only show weeks somebody sent. This shows the weeks
 * nobody sent, which are the ones that matter: until this existed the only thing
 * that knew was a cron job that mails about it, and 68 of 75 people were four
 * weeks behind with no screen able to say so.
 *
 * A grid rather than a list, because the question is never about one week - it is
 * "who has a run of empty ones", and a run is a shape you see rather than read.
 */
export function SubmissionBoard() {
  const [weeks, setWeeks] = React.useState(8);
  const { data, isPending, isError, error } = useApprovalBoard(weeks);

  if (isPending) return <Skeleton className="h-72 w-full" />;
  if (isError) {
    return (
      <Alert className="border-destructive/30">
        <AlertDescription className="text-sm">
          {error instanceof Error ? error.message : 'Could not load the board.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (data.rows.length === 0) {
    return (
      <Alert>
        <AlertDescription className="text-sm">
          Nobody with a working contract is visible to you, so there is nothing to chase.
        </AlertDescription>
      </Alert>
    );
  }

  // Worst first: this screen exists to be acted on, and the person eight weeks
  // behind is not the one alphabetical order puts at the top.
  const rows = [...data.rows].sort(
    (a, b) => b.missing - a.missing || a.employee.displayName.localeCompare(b.employee.displayName),
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm">
          <strong>{data.behind}</strong> of {data.rows.length}{' '}
          {data.rows.length === 1 ? 'person has' : 'people have'} a week outstanding.
        </p>
        <div className="flex items-center gap-1">
          {[4, 8, 13, 26].map((n) => (
            <Button
              key={n}
              hint={`Show the last ${n} weeks`}
              variant={n === weeks ? 'default' : 'outline'}
              size="sm"
              onClick={() => setWeeks(n)}
            >
              {n} weeks
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-x-auto rounded-lg border">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="bg-muted">
              <th className="sticky left-0 z-10 bg-muted px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Person
              </th>
              {data.weeks.map((week) => (
                <th
                  key={week}
                  className="px-1 py-2 text-center text-[11px] font-semibold uppercase tracking-wider text-muted-foreground"
                  title={`Week beginning ${week}`}
                >
                  {shortWeek(week)}
                </th>
              ))}
              <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                Behind
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.employee.id} className="border-t">
                <td className="sticky left-0 z-10 whitespace-nowrap bg-background px-3 py-1.5 font-medium">
                  {row.employee.displayName}
                </td>
                {row.cells.map((cell) => (
                  <td key={cell.weekStart} className="px-1 py-1.5 text-center">
                    <Cell cell={cell} />
                  </td>
                ))}
                <td
                  className={`ts-num px-3 py-1.5 text-right font-semibold ${
                    row.missing > 0 ? 'text-destructive' : 'text-muted-foreground'
                  }`}
                >
                  {row.missing || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
        <Legend className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400">approved</Legend>
        <Legend className="bg-sky-500/15 text-sky-700 dark:text-sky-400">submitted</Legend>
        <Legend className="bg-destructive/15 text-destructive">not submitted</Legend>
        <Legend className="bg-amber-500/15 text-amber-700 dark:text-amber-500">sent back</Legend>
        <span>· a blank cell is a week the contract expected no work in, or one still in progress</span>
        <span>· a week crossing a month end shows its two parts side by side</span>
      </div>
    </div>
  );
}

/**
 * One person-week.
 *
 * Colour carries the status and the number carries the hours, so the grid can be
 * skimmed for shape and still answer "how much" without a second screen.
 *
 * A week split at a month end shows its two parts side by side, each in its own
 * colour: September submitted and October not yet over is a different story from
 * the whole week missing, and one colour for both would hide it.
 */
function Cell({ cell }: { cell: BoardCell }) {
  const today = orgToday();
  if (cell.parts.length > 1) {
    return (
      <span className="flex gap-0.5">
        {cell.parts.map((part) => (
          <span key={part.start} className="min-w-0 flex-1">
            <Chip
              status={part.status}
              actualSeconds={part.actualSeconds}
              expectedSeconds={part.expectedSeconds}
              what={formatPeriod(part)}
              notOver={part.end >= today}
            />
          </span>
        ))}
      </span>
    );
  }
  return (
    <Chip
      status={cell.status}
      actualSeconds={cell.actualSeconds}
      expectedSeconds={cell.expectedSeconds}
      what={`week beginning ${cell.weekStart}`}
      notOver={(cell.parts[0]?.end ?? '') >= today}
    />
  );
}

function Chip({
  status,
  actualSeconds,
  expectedSeconds,
  what,
  notOver,
}: {
  status: BoardCell['status'];
  actualSeconds: number;
  expectedSeconds: number;
  what: string;
  /** A part of this week that has not ended yet, so it cannot be late. */
  notOver: boolean;
}) {
  if (status === 'NOT_APPLICABLE') {
    return (
      <span
        className="block h-6 w-full rounded bg-muted/40"
        title={notOver ? `${what} — in progress` : 'No work expected'}
      />
    );
  }

  const tone =
    status === 'APPROVED'
      ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-400'
      : status === 'SUBMITTED'
        ? 'bg-sky-500/15 text-sky-700 dark:text-sky-400'
        : status === 'DENIED'
          ? 'bg-amber-500/15 text-amber-700 dark:text-amber-500'
          : 'bg-destructive/15 text-destructive';

  return (
    <span
      className={`ts-num block truncate rounded px-1 py-0.5 text-[11px] font-medium tabular-nums ${tone}`}
      title={`${label(status)} — ${formatDuration(actualSeconds)} recorded of ${formatDuration(
        expectedSeconds,
      )} expected, ${what}`}
    >
      {actualSeconds > 0 ? formatDuration(actualSeconds) : '—'}
    </span>
  );
}

function Legend({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className="flex items-center gap-1.5">
      <span className={`inline-block size-3 rounded ${className}`} />
      {children}
    </span>
  );
}

function label(status: BoardCell['status']): string {
  return status === 'NOT_SUBMITTED'
    ? 'Not submitted'
    : status === 'SUBMITTED'
      ? 'Waiting for approval'
      : status === 'APPROVED'
        ? 'Approved'
        : status === 'DENIED'
          ? 'Sent back'
          : 'No work expected';
}

/** "8 Sep" - the Monday. Enough to find a week, short enough for a column head. */
function shortWeek(week: string): string {
  return new Date(`${week}T00:00:00.000Z`).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}
